/**
 * cart-substitute.js
 * "Allow substitute" checkbox — optimised, zero-lag strategy:
 *
 *   CHECKBOX CHANGE  → in-memory update only (instant, no network)
 *   NEW ITEM ADDED   → MutationObserver detects new keys, flushes to server
 *   ITEM REMOVED     → orphaned keys cleaned from pendingStates + sessionStorage
 *   QUANTITY CHANGE  → re-stamps in-memory states onto re-rendered DOM
 *   CHECKOUT CLICK   → single batched /cart/update.js, then proceeds to checkout
 *   PAGE UNLOAD      → sendBeacon flush so server state survives navigation
 *
 * ── Fix log (round 2) ────────────────────────────────────────────────────────
 *
 *  FIX A · Observer was watching CartDrawer-CartItems / main-cart-items
 *          directly.  Shopify's Section Rendering API replaces those elements'
 *          innerHTML wholesale on every cart change, which detaches the observer
 *          from the live DOM — it silently watches a ghost node forever after
 *          the first add/quantity change.
 *          Fix: climb to the nearest stable ancestor (#CartDrawer, #cart,
 *          cart-drawer, etc.) so the observer survives section re-renders.
 *          Fall back to document.body only if nothing better is found.
 *
 *  FIX B · pruneRemovedItems() was called immediately after each mutation with
 *          whatever getCurrentAttrKeys() returned at that instant.  During the
 *          brief window when Shopify has torn down the old HTML but not yet
 *          stamped the new HTML, getCurrentAttrKeys() returns an empty Set.
 *          Passing an empty Set to prune deleted every entry in pendingStates,
 *          wiping all checkbox selections silently.
 *          Fix: skip pruning entirely when the returned key set is empty.
 *
 *  FIX C · A single reapplyToDom() after the 80 ms debounce was not enough.
 *          Shopify themes (Dawn and derivatives) commonly do two renders per
 *          cart action: an optimistic in-memory render immediately, then a
 *          second authoritative render after the /cart/change.js (or /add.js)
 *          round-trip completes.  The debounce collapsed both mutations into
 *          one handler call, which ran after the first render but before the
 *          second — leaving the final DOM un-stamped.
 *          Fix: after the primary reapplyToDom(), schedule a trailing reapply
 *          at 350 ms to catch the second render without needing to know the
 *          theme's exact timing.
 *
 *  (Previous fixes retained)
 *  FIX 1 · init() calls reapplyToDom() so sessionStorage-restored states are
 *          immediately reflected in the DOM on every page load / navigation.
 *  FIX 2 · pagehide sends a sendBeacon flush so the server stays in sync
 *          before the page tears down.
 *  FIX 3 · MutationObserver handler awaits flushToCart() before reapplyToDom()
 *          to prevent the flush-response re-render from wiping re-stamped boxes.
 */

(function () {
  'use strict';

  // ─── In-memory state ────────────────────────────────────────────────────────
  const pendingStates = {};
  const SESSION_KEY = 'cart_substitute_pending';

  // ─── sessionStorage persistence ─────────────────────────────────────────────
  function savePendingStates() {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(pendingStates)); } catch (e) {}
  }

  function loadPendingStates() {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) Object.assign(pendingStates, JSON.parse(saved));
    } catch (e) {}
  }

  // ─── DOM helpers ─────────────────────────────────────────────────────────────
  function getWrapperData(wrapper) {
    return { attrKey: wrapper.dataset.attrKey };
  }

  function getCurrentAttrKeys() {
    return new Set(
      [...document.querySelectorAll('[data-substitute-wrapper]')]
        .map((w) => w.dataset.attrKey)
        .filter(Boolean)
    );
  }

  // ─── Seed from current DOM ───────────────────────────────────────────────────
  // Only seeds keys not already in pendingStates (preserves user-toggled state).
  // Value is the product label (from data-item-label), not a plain 'yes', so
  // the fulfilment team sees the product name in Admin > Additional details.
  function seedFromDOM() {
    document.querySelectorAll('[data-substitute-checkbox]').forEach((cb) => {
      const wrapper = cb.closest('[data-substitute-wrapper]');
      if (!wrapper) return;
      const { attrKey } = getWrapperData(wrapper);
      if (!(attrKey in pendingStates)) {
        pendingStates[attrKey] = cb.checked ? (wrapper.dataset.itemLabel || cb.value) : '';
      }
    });
  }

  // ─── Re-stamp in-memory states onto re-rendered DOM ──────────────────────────
  function reapplyToDom() {
    document.querySelectorAll('[data-substitute-checkbox]').forEach((cb) => {
      const wrapper = cb.closest('[data-substitute-wrapper]');
      if (!wrapper) return;
      const { attrKey } = getWrapperData(wrapper);
      if (attrKey in pendingStates) {
        cb.checked = pendingStates[attrKey] !== '';
      }
    });
    seedFromDOM();
  }

  // ─── FIX B: Safe prune — never wipe states during a mid-swap empty window ───
  // When Shopify's Section Rendering API replaces cart HTML it briefly removes
  // the old nodes before inserting new ones.  getCurrentAttrKeys() returns an
  // empty Set during that window.  Passing {} to the original prune deleted
  // every pendingStates entry, silently losing all selections.
  function pruneRemovedItems(currentKeys) {
    // Empty set can mean "mid-render teardown" — skip to be safe.
    if (currentKeys.size === 0) return;

    let changed = false;
    for (const key of Object.keys(pendingStates)) {
      if (!currentKeys.has(key)) {
        delete pendingStates[key];
        changed = true;
      }
    }
    if (changed) savePendingStates();
  }

  // ─── Checkbox change → memory + sessionStorage only, zero network lag ─────────
  // Stores the product label (data-item-label) as the value when checked so
  // the fulfilment team sees the product name, not a meaningless 'yes'.
  function handleChange(event) {
    const cb = event.target;
    const wrapper = cb.closest('[data-substitute-wrapper]');
    if (!wrapper) return;
    pendingStates[wrapper.dataset.attrKey] = cb.checked
      ? (wrapper.dataset.itemLabel || cb.value)
      : '';
    savePendingStates();
  }

  // ─── Batch flush to cart ──────────────────────────────────────────────────────
  // Only attributes with a non-empty value (i.e. ticked checkboxes) are written.
  // Attributes that were previously written but are now empty are explicitly
  // cleared by sending an empty string — Shopify removes them from the cart.
  // The net result in Admin > Additional details is exactly one line per ticked
  // product, showing the product label.  Unticked items produce no output at all.
  async function flushToCart() {
    const activeKeys = getCurrentAttrKeys();
    const attributes = {};
    for (const [key, value] of Object.entries(pendingStates)) {
      if (activeKeys.has(key)) {
        // Always include the key: non-empty = write label, empty = clear attribute.
        attributes[key] = value;
      }
    }
    if (Object.keys(attributes).length === 0) return;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) throw new Error(`[cart-substitute] Flush failed: ${response.status}`);
    return response.json();
  }

  // ─── FIX 2: Beacon flush on page unload ──────────────────────────────────────
  function flushOnUnload() {
    const activeKeys = getCurrentAttrKeys();
    const attributes = {};
    for (const [key, value] of Object.entries(pendingStates)) {
      if (activeKeys.has(key)) attributes[key] = value;
    }
    if (Object.keys(attributes).length === 0) return;

    const blob = new Blob([JSON.stringify({ attributes })], {
      type: 'application/json',
    });
    navigator.sendBeacon('/cart/update.js', blob);
  }

  // ─── Checkout interception ────────────────────────────────────────────────────
  function interceptCheckout() {
    document.addEventListener(
      'click',
      async function (event) {
        const btn = event.target.closest('[name="checkout"]');
        if (!btn) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        btn.disabled = true;
        const originalText = btn.textContent;
        btn.textContent = 'Please wait…';

        try {
          await flushToCart();
        } catch (err) {
          console.error('[cart-substitute] Could not save preferences:', err);
        } finally {
          btn.disabled = false;
          btn.textContent = originalText;
        }

        if (btn.form) {
          const clone = document.createElement('input');
          clone.type = 'hidden';
          clone.name = 'checkout';
          clone.value = '';
          btn.form.appendChild(clone);
          btn.form.submit();
          clone.remove();
        } else {
          window.location.href = '/checkout';
        }
      },
      true
    );
  }

  // ─── FIX A: Resolve stable ancestors to observe ──────────────────────────────
  // CartDrawer-CartItems and main-cart-items are replaced wholesale by Shopify's
  // Section Rendering API on every cart mutation.  Attaching a MutationObserver
  // directly to them means the observer silently watches a detached node after
  // the first replace — it never fires again for any subsequent changes.
  //
  // Strategy: walk up from the known items containers to find the nearest element
  // that Shopify never replaces (the cart drawer shell, the cart page wrapper,
  // a custom element, etc.).  Fall back to document.body only as a last resort.
  function resolveStableAncestors() {
    const itemContainerIds = ['CartDrawer-CartItems', 'main-cart-items'];
    const stableSet = new Set();

    for (const id of itemContainerIds) {
      const el = document.getElementById(id);
      if (!el) continue;
      // Walk up until we find a meaningful stable wrapper, or stop at <body>.
      let candidate = el.parentElement;
      while (candidate && candidate !== document.body) {
        const tag = candidate.tagName.toLowerCase();
        const cid = (candidate.id || '').toLowerCase();
        const cls = (candidate.className || '').toLowerCase();
        // Stop at known stable shells or custom elements (contain a hyphen).
        if (
          tag.includes('-') ||           // custom element (e.g. cart-drawer)
          cid.includes('cart') ||        // #CartDrawer, #cart, etc.
          cls.includes('cart-drawer') || // class-based wrappers
          candidate === el.parentElement // immediate parent as minimum
        ) {
          stableSet.add(candidate);
          break;
        }
        candidate = candidate.parentElement;
      }
    }

    // Named stable shells that are never section-rendered (Dawn + common themes)
    ['CartDrawer', 'cart']
      .map((id) => document.getElementById(id))
      .filter(Boolean)
      .forEach((el) => stableSet.add(el));

    // Custom elements whose tagNames contain "cart"
    document.querySelectorAll('cart-drawer, cart-notification').forEach((el) =>
      stableSet.add(el)
    );

    return stableSet.size > 0 ? [...stableSet] : [document.body];
  }

  // ─── MutationObserver ────────────────────────────────────────────────────────
  function watchCartRerenders() {
    // FIX A: watch stable ancestors, not the items containers themselves.
    const targets = resolveStableAncestors();

    let knownKeys = getCurrentAttrKeys();
    let debounceTimer = null;
    let trailingTimer = null;

    const debouncedHandler = () => {
      clearTimeout(debounceTimer);
      clearTimeout(trailingTimer);

      debounceTimer = setTimeout(async () => {
        const currentKeys = getCurrentAttrKeys();

        // New item added → flush pending states to server before Shopify's own
        // re-render overwrites the section.  Await so reapplyToDom runs only
        // after the server has acknowledged the new attributes.  (FIX 3)
        const hasNewItems = [...currentKeys].some((k) => !knownKeys.has(k));
        if (hasNewItems) {
          await flushToCart().catch(() => {});
        }

        // FIX B: only prune when keys are non-empty (not mid-teardown)
        pruneRemovedItems(currentKeys);

        knownKeys = currentKeys;
        reapplyToDom();

        // FIX C: trailing reapply to catch Shopify's second authoritative
        // section render, which arrives after the /cart/change.js round-trip
        // and fires AFTER our debounce has already settled.
        trailingTimer = setTimeout(reapplyToDom, 350);
      }, 80);
    };

    const observer = new MutationObserver((mutations) => {
      const hasStructural = mutations.some(
        (m) =>
          m.type === 'childList' &&
          (m.addedNodes.length > 0 || m.removedNodes.length > 0)
      );
      if (hasStructural) debouncedHandler();
    });

    targets.forEach((t) => observer.observe(t, { childList: true, subtree: true }));
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    loadPendingStates();
    seedFromDOM();
    reapplyToDom(); // FIX 1: push restored states into DOM immediately

    watchCartRerenders();
    interceptCheckout();

    document.addEventListener(
      'change',
      function (event) {
        if (event.target.matches('[data-substitute-checkbox]')) {
          event.stopPropagation();
          handleChange(event);
        }
      },
      true
    );

    window.addEventListener('pagehide', flushOnUnload); // FIX 2
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();